import React, { createContext, useContext, useRef, useState } from 'react';

import { RecommendedGroupPicker } from '../index';
import type { RecommendedGroup, RecommendedGroupPickerProps } from '../types';

type RecommendedGroupPickerPropsInternal = Omit<RecommendedGroupPickerProps, 'isOpen' | 'onGroupSelected' | 'onClose'>;

type RecommendedGroupPickerContextType = {
	startGroupSelection: (props: RecommendedGroupPickerPropsInternal) => Promise<RecommendedGroup | undefined>;
	stopGroupSelection: () => void;
};

const RecommendedGroupPickerContext = createContext<RecommendedGroupPickerContextType>({
	startGroupSelection: async (_props: RecommendedGroupPickerPropsInternal) => Promise.resolve(undefined),
	stopGroupSelection: () => {},
});

export const useRecommendedGroupPickerContext = () => useContext(RecommendedGroupPickerContext);

export const RecommendedGroupPickerProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const [recommendedGroupPickerProps, setRecommendedGroupPickerProps] = useState<RecommendedGroupPickerProps | null>(
		null,
	);

	const resolveRef = useRef<(value: RecommendedGroup | undefined) => void>();

	/*
	 * @example
	 *
	 * const { startGroupSelection } = useRecommendedGroupPickerContext();
	 * const selectedGroup = await startGroupSelection({ platform: 'facebook', recommendedGroups: [...] });
	 * if (selectedGroup) {
	 *   // User selected a group
	 * } else {
	 *   // User cancelled the selection
	 * }
	 */
	const startGroupSelection = async (
		props: RecommendedGroupPickerPropsInternal,
	): Promise<RecommendedGroup | undefined> => {
		// eslint-disable-next-line promise/avoid-new
		return new Promise((resolve) => {
			resolveRef.current = resolve;
			setRecommendedGroupPickerProps({
				...props,
				isOpen: true,
				onGroupSelected: (recommendedGroup: RecommendedGroup | undefined) => {
					resolve(recommendedGroup);
					setRecommendedGroupPickerProps(null);
				},
				onClose: () => {
					resolve(undefined);
					setRecommendedGroupPickerProps(null);
				},
			});
		});
	};

	const stopGroupSelection = () => {
		resolveRef.current?.(undefined);
		setRecommendedGroupPickerProps(null);
	};

	return (
		<RecommendedGroupPickerContext.Provider
			value={{ startGroupSelection, stopGroupSelection }}
			data-testid="recommended-group-picker-context"
		>
			{children}
			{recommendedGroupPickerProps && <RecommendedGroupPicker {...recommendedGroupPickerProps} />}
		</RecommendedGroupPickerContext.Provider>
	);
};
